import { API_BASE, getToken } from "./api.js";

/**
 * Live commentary, spoken into the phone.
 *
 * Press and hold, talk, let go. What you said is attached to the ball that was
 * just recorded and appears in the feed for anyone watching — a human voice
 * over the top of the generated lines rather than instead of them.
 *
 * Push-to-talk rather than an open mic on purpose. A scorer at a ground is
 * standing among people who are also talking, and an open mic would broadcast
 * all of it. Holding a button is also the only interaction that reliably means
 * "I meant to say that".
 *
 * Two things this cannot do, and it is better to say so than to pretend:
 * it is not a live stream — a spectator hears the clip once it has uploaded,
 * which on a poor connection may be a while — and it needs the microphone
 * permission the site's own Permissions-Policy header currently withholds.
 */

export type MicState = "idle" | "asking" | "recording" | "sending" | "blocked" | "unsupported";

let media: MediaStream | null = null;
let recorder: MediaRecorder | null = null;
let chunks: Blob[] = [];
let startedAt = 0;
let listeners: ((s: MicState, detail?: string) => void)[] = [];
let state: MicState = "idle";

export function onMic(fn: (s: MicState, detail?: string) => void): () => void {
  listeners.push(fn);
  fn(state);
  return () => { listeners = listeners.filter((f) => f !== fn); };
}

function set(s: MicState, detail?: string): void {
  state = s;
  for (const fn of listeners) fn(s, detail);
}

export function getState(): MicState { return state; }

export function supported(): boolean {
  return typeof navigator !== "undefined"
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== "undefined";
}

/**
 * The container the browser will actually produce.
 *
 * Safari gives mp4/AAC, Chrome and Firefox give WebM/Opus. Asking for a type
 * the browser cannot encode fails at record time rather than here, so pick from
 * what it admits to supporting.
 */
function mimeType(): string {
  const wanted = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const t of wanted) {
    if (MediaRecorder.isTypeSupported?.(t)) return t;
  }
  return "";
}

/**
 * The live microphone, for the WebRTC broadcaster.
 *
 * The same stream the recorder uses, on purpose. A second getUserMedia call
 * would be a second permission prompt on some browsers and a second echo
 * canceller on all of them, and the two would fight.
 *
 * A track can feed a MediaRecorder and a peer connection at once, which is
 * what makes "live for whoever can hear it, recorded for everyone" one press
 * rather than two.
 */
export async function acquire(): Promise<MediaStream | null> {
  return stream();
}

/**
 * Does the page's own policy permit a microphone at all?
 *
 * Separate from whether the user has granted it. A Permissions-Policy header
 * saying microphone=() makes getUserMedia fail before a prompt is ever shown,
 * and no browser setting overrides it.
 *
 * Only Chromium exposes this reliably. Where it is unknown, assume allowed —
 * a wrong guess here only costs a less specific error message, whereas
 * assuming blocked would mislead everyone whose browser simply does not
 * implement the API.
 */
function policyAllowsMic(): boolean {
  try {
    const d = document as unknown as {
      featurePolicy?: { allowsFeature(f: string): boolean };
      permissionsPolicy?: { allowsFeature(f: string): boolean };
    };
    const policy = d.permissionsPolicy ?? d.featurePolicy;
    if (!policy?.allowsFeature) return true;
    return policy.allowsFeature("microphone");
  } catch {
    return true;
  }
}

/** Ask once and keep the stream: a permission prompt per ball would be absurd. */
async function stream(): Promise<MediaStream | null> {
  if (media) return media;
  set("asking");
  try {
    media = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    return media;
  } catch (e) {
    const name = (e as { name?: string }).name ?? "";
    // A Permissions-Policy header blocking the microphone rejects with
    // NotAllowedError before any prompt is shown, which looks identical to the
    // user refusing — so say what is actually likely.
    /**
     * Tell the truth about which kind of "no" this was.
     *
     * NotAllowedError covers two situations that need opposite responses, and
     * the old message ran them together — so a scorer was told to allow the
     * microphone in a case where no amount of allowing would help.
     *
     * If the page's permissions policy forbids the microphone, the browser
     * refuses before any prompt appears. Nothing the person taps can change
     * that; it is a header on the response and it has to be fixed by whoever
     * runs the site. Saying "allow it for this site" there is advice that
     * cannot be followed.
     */
    set("blocked",
      name === "NotAllowedError" && !policyAllowsMic()
        ? "This site's settings block the microphone, so it cannot be turned on here. "
          + "The Android app records fine. On the web it needs a server change."
      : name === "NotAllowedError"
        ? "The microphone is blocked. Allow it for this site in your browser settings."
      : name === "NotFoundError" ? "No microphone found on this device."
      : `The microphone could not start (${name || "unknown"}).`);
    return null;
  }
}

export async function start(): Promise<boolean> {
  if (!supported()) { set("unsupported"); return false; }
  if (state === "recording") return true;

  const s = await stream();
  if (!s) return false;

  chunks = [];
  const type = mimeType();
  try {
    recorder = new MediaRecorder(s, type ? { mimeType: type } : undefined);
  } catch {
    set("blocked", "This browser cannot record audio.");
    return false;
  }
  recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  recorder.start();
  startedAt = Date.now();
  set("recording");
  return true;
}

export interface Clip { blob: Blob; seconds: number; type: string }

/** Stop, and hand back what was said. Null if it was too short to be speech. */
export async function stop(): Promise<Clip | null> {
  if (!recorder || recorder.state === "inactive") { set("idle"); return null; }

  const seconds = (Date.now() - startedAt) / 1000;
  const done = new Promise<void>((res) => {
    recorder!.onstop = () => res();
  });
  recorder.stop();
  await done;

  const type = recorder.mimeType || "audio/webm";
  recorder = null;
  set("idle");

  // Under half a second is a mis-tap, not commentary.
  if (seconds < 0.5 || !chunks.length) return null;
  return { blob: new Blob(chunks, { type }), seconds, type };
}

/** Give the microphone back to the phone when the scorer leaves. */
export function release(): void {
  try { recorder?.stop(); } catch { /* already stopped */ }
  recorder = null;
  media?.getTracks().forEach((t) => t.stop());
  media = null;
  set("idle");
}

/**
 * Send a clip, tagged with where in the match it belongs.
 *
 * The over and ball are what let the feed put it in the right place rather than
 * simply at the end.
 */
export async function upload(
  matchId: string, clip: Clip, at: { over: number; ball: number },
): Promise<{ ok: boolean; id?: string; error?: string }> {
  const token = getToken();
  try {
    const r = await fetch(
      `${API_BASE}/api/v1/matches/${matchId}/voice`
      + `?over=${at.over}&ball=${at.ball}&seconds=${clip.seconds.toFixed(1)}`,
      {
        method: "POST",
        headers: {
          "content-type": clip.type,
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: clip.blob,
      },
    );
    const d = (await r.json()) as { id?: string; error?: string };
    return r.ok ? { ok: true, ...(d.id ? { id: d.id } : {}) }
                : { ok: false, error: d.error ?? `Upload failed (${r.status}).` };
  } catch {
    return { ok: false, error: "Could not send that — you may be offline." };
  }
}
